/**
 * LiveTrackingDialog — start following a runner's live splits against this race prediction.
 *
 * Two providers, because they publish live data differently:
 *   UTMB — every runner has a public page, so the link is pasted.
 *   TORX — no per-runner page exists (which is also why their share button does nothing), so the
 *          race is picked and the bib typed; the server validates it against the entrant list.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Box,
} from '@mui/material';
import { attachLiveTracking, attachTorxLiveTracking, getTorxRaces } from '../../../api/runningApi';

export default function LiveTrackingDialog({ open, onClose, predictionId, clubPredictionId, onAttached }) {
  const [provider, setProvider] = useState('utmb');
  const [url, setUrl] = useState('');
  const [raceId, setRaceId] = useState('');
  const [bib, setBib] = useState('');
  const [races, setRaces] = useState([]);
  const [racesLoading, setRacesLoading] = useState(false);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load the TORX race list lazily — only when that tab is actually opened.
  useEffect(() => {
    if (!open || provider !== 'torx' || races.length || racesLoading) return;
    setRacesLoading(true);
    getTorxRaces()
      .then((data) => { setRaces(data?.races || []); setYear(data?.year || null); })
      .catch(() => setError('Could not load the TORX race list — try again in a moment.'))
      .finally(() => setRacesLoading(false));
  }, [open, provider, races.length, racesLoading]);

  const handleClose = () => {
    setUrl('');
    setRaceId('');
    setBib('');
    setError(null);
    onClose();
  };

  const canSubmit = provider === 'utmb' ? !!url.trim() : (!!raceId && !!bib.trim());

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = provider === 'torx'
        ? await attachTorxLiveTracking(predictionId, raceId, bib.trim(), year, clubPredictionId || null)
        : await attachLiveTracking(predictionId, url.trim(), clubPredictionId || null);
      onAttached(result);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || (provider === 'torx'
        ? 'Could not follow that runner — check the race and bib and try again.'
        : 'Could not follow that link — check the URL and try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Follow Live</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={provider}
          onChange={(e, v) => { if (v) { setProvider(v); setError(null); } }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="utmb">UTMB</ToggleButton>
          <ToggleButton value="torx">TORX</ToggleButton>
        </ToggleButtonGroup>

        {provider === 'utmb' ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Paste the UTMB Live runner link to blend live splits with this race prediction.
            </Typography>
            <TextField
              autoFocus
              label="UTMB Live runner link"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
              placeholder="https://live.utmb.world/en/utmb/2026/runners/1012"
            />
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              TORX has no per-runner link, so pick the race and enter the bib.
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                select
                label="Race"
                value={raceId}
                onChange={(e) => setRaceId(e.target.value)}
                sx={{ minWidth: 220, flex: 1 }}
                disabled={racesLoading}
                helperText={racesLoading ? 'Loading races…' : (year ? `${year} season` : ' ')}
              >
                {races.map((r) => (
                  <MenuItem key={r.gara_id} value={r.gara_id}>
                    {r.name} — {r.entrants} entrants
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Bib"
                value={bib}
                onChange={(e) => setBib(e.target.value)}
                sx={{ width: 140 }}
                placeholder="2189"
                helperText=" "
              />
            </Box>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Following...' : 'Follow'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
